/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomRadioButtonV3.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import InfoButton from "../../../infoButton/InfoButton";

const getCheckedColor = (disabled, error) => {
  if (disabled) return "#6f7174";
  if (error) return "red";
  return "#26890D";
};

const getUncheckedBorderColor = (disabled, error) => {
  if (disabled) return "#6f7174";
  if (error) return "red";
  return "black";
};

function CustomRadioButtonV3({
  error = false,
  value = "",
  onChange,
  data = [],
  label,
  required = false,
  infoname,
  disabled,
  onlyLR = false,
  sx = {},
}) {
  const toSentenceCase = (str = "") => {
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ""))
      .join(" ");
  };

  const checkedColor = getCheckedColor(disabled, error);
  const uncheckedBorderColor = getUncheckedBorderColor(disabled, error);

  return (
    <Box sx={{ width: "100%", ...sx }}>
      <Box sx={{ display: "flex" }}>
        {label && (
          <Typography
            sx={{
              mb: 0.8,
              color: "#6f7174",
              fontSize: "0.9rem",
              fontWeight: 500,
              display: "block",
            }}
          >
            {label}
            {required && <span style={{ color: "red" }}> *</span>}
          </Typography>
        )}
        <Box sx={{ width: 5, height: 5 }}>
          {infoname?.length > 0 ? (
            <InfoButton
              dimension={{ width: 15, height: 15 }}
              infoKey={infoname}
              color={"black"}
            />
          ) : null}
        </Box>
      </Box>

      <RadioGroup row value={value} onChange={onChange} aria-invalid={error}>
        {data.map((item, index) => (
          <FormControlLabel
            disabled={disabled}
            key={item.value || item.id || item.label || index}
            value={item.value}
            label={
              <Typography sx={{ ml: 0.5, color: disabled ? "#6f7174" : "inherit" }}>
                {onlyLR ? item?.label : toSentenceCase(item?.label)}
              </Typography>
            }
            sx={{
              m: 1,
              "& .MuiFormControlLabel-label": {
                fontSize: "0.9rem",
                fontWeight: 500,
              },
            }}
            control={
              <Radio
                disabled={disabled}
                disableRipple
                checkedIcon={
                  <Box
                    className="checked-box"
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: checkedColor,
                      border: `2px solid ${checkedColor}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: "white",
                      }}
                    />
                  </Box>
                }
                icon={
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border: `2px solid ${uncheckedBorderColor}`,
                      transition: "all 0.2s ease",
                    }}
                  />
                }
                sx={{
                  p: 0,
                  "&:hover": {
                    backgroundColor: "#f3f3f3",
                  },
                  "&:hover .checked-box": {
                    backgroundColor: error ? "#992A22" : "#1a5d09",
                    borderColor: error ? "#992A22" : "#1a5d09",
                  },
                }}
              />
            }
          />
        ))}
      </RadioGroup>
    </Box>
  );
}

CustomRadioButtonV3.propTypes = {
  error: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  onChange: PropTypes.func,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
      label: PropTypes.string,
    })
  ),
  label: PropTypes.node,
  required: PropTypes.bool,
  infoname: PropTypes.string,
  disabled: PropTypes.bool,
  onlyLR: PropTypes.bool,
  sx: PropTypes.object,
};

export default CustomRadioButtonV3;
